import { Affiliate, AffiliateRepresentationType } from '@/interfaces/affiliates';
import {
  uploadRepresentative,
  queryRepresentativesTypes,
} from '@/services/representatives.service';
import { getAffiliate, queryAffiliates } from '@/services/affiliates.service';

import { Effect, Reducer } from '@umijs/max';
import { getUsers } from '@/services/user.service';
import { User } from '@/interfaces/user';
import { Dayjs } from 'dayjs';
import { notification, Modal } from 'antd';

export interface StateType {
  current?: string;
  step?: {
    fileList: any[];
    dni?: string;
    name?: string;
    email?: string;
    comments?: string;
    expiry_date?: Dayjs;
    phone_number_1?: string;
    file: { file: string; pk: number } | null;
    user?: { value: number; label: string[]; key: number };
    affiliate?: { value: number; label: string[]; key: number };
    representations_list?: any[];
    representations_type?: number;
  };
  users: User[];
  affiliate: Affiliate | null;
  success: boolean;
  submitting: boolean;
  affiliates: Affiliate[];
  representativeTypes: AffiliateRepresentationType[];
  isFetchingAffiliates: boolean;
  representative: number | null;
}

export interface ModelType {
  namespace: string;
  state: StateType;
  effects: {
    getRepresentationType: Effect;
    submitStepForm: Effect;
    getAffiliates: Effect;
    setAffiliate: Effect;
    getUsers: Effect;
    reset: Effect;
  };
  reducers: {
    setSubmitting: Reducer<any>;
    setSuccess: Reducer<any>;
    setRepresentativeTypes: Reducer<any>;
    setRepresentative: Reducer<any>;
    saveStepFormData: Reducer<any>;
    saveCurrentStep: Reducer<any>;
    saveAffiliate: Reducer<any>;
    setLoadingAffiliates: Reducer<any>;
    saveAffiliates: Reducer<any>;
    setLoadingUsers: Reducer<any>;
    saveUsers: Reducer<any>;
    resetState: Reducer<any>;
  };
}

const Model: ModelType = {
  namespace: 'newRepresentative',

  state: {
    step: {
      file: null,
      fileList: [],
    },
    users: [],
    affiliates: [],
    affiliate: null,
    submitting: false,
    success: false,
    current: 'representation',
    representativeTypes: [],
    isFetchingAffiliates: false,
    representative: null,
  },

  effects: {
    *submitStepForm(_, { call, put, select }) {
      const res = yield select(
        ({ newRepresentative } : { newRepresentative: StateType }) => newRepresentative.step,
      );
      
      yield put({
        type: 'setSubmitting',
        payload: true,
      });
      try {
        const { file, affiliate, user, ...fields } = res;

        const payload = {
          ...fields,
          affiliate_id: affiliate,
          file_id: file ? file.pk : undefined,
          user_id: user ? user.value : undefined,
          representation_type_id: res.representation_type,
          expiry_date: res.expiry_date ? res.expiry_date.format('YYYY-MM-DD') : undefined,
          observations: 'Creación inicial de la representación/autorización',
          user: !user
            ? {
                dni: fields.dni,
                email: fields.email,
                person: {
                  dni: fields.dni,
                  name: fields.name,
                  email: fields.email,
                  phone_number_1: fields.phone_number_1,
                },
              }
            : undefined,
        };
        const response = yield call(uploadRepresentative, payload);

        yield put({
          type: 'setSuccess',
          payload: response.data,
        });
        yield put({
          type: 'setRepresentative',
          payload: response.data.id
        })
      } catch (error) {
        if (
          error &&
          (error as any).data?.non_field_errors &&
          (error as any).data?.non_field_errors.includes(
            'The affiliate exceeded the allowed limit of representatives',
          )
        ) {
          Modal.error({
            centered: true,
            title: 'Lo sentimos',
            content:
              'Parece que este afiliado ya superó el máximo permitido de representantes activos.',
          });
        } else {
          const err = (error as any)?.response?.data?.detail?.[0];
          if (err) {
            notification.error({ message: err })
          } else notification.error({ message: err.response.data.detail || 'Ocurrió un error.' });
        }
      }

      yield put({
        type: 'setSubmitting',
        payload: false,
      });
    },
    *setAffiliate({ payload }, { call, put }) {
        const response = yield call(getAffiliate, payload);
        yield put({
          type: 'saveAffiliate',
          payload: response.data,
        });
      },
    *reset(_, { put }) {
      yield put({
        type: 'resetState',
      });
    },

    *getRepresentationType({ payload }, { call, put }) {
      const response = yield call(queryRepresentativesTypes, payload);
      yield put({
        type: 'setRepresentativeTypes',
        payload: response.data,
      });
    },

    *getAffiliates({ payload }, { call, put }) {
      
      yield put({
        type: 'setLoadingAffiliates',
        payload: true,
      });
      
      try {
        const response = yield call(queryAffiliates, payload);
        
        yield put({
          type: 'saveAffiliates',
          payload: response.data,
        });
      } catch (error: any) {
        notification.error({ message: error?.response?.data?.detail ?? 'Error al buscar afiliados.' });
        yield put({ type: 'setLoadingAffiliates', payload: false });
      }
    },

    *getUsers({ payload }, { call, put }) {
      yield put({
        type: 'setLoadingUsers',
        payload: true,
      });

      try {
        const response = yield call(getUsers, payload);
        yield put({
          type: 'saveUsers',
          payload: response.data,
        });
      } catch (error) {
        yield put({
          type: 'setLoadingUsers',
          payload: false,
        });
      }
    },
  },

  reducers: {
    resetState() {
      return {
        step: {
          file: null,
          fileList: [],
        },
        users: [],
        affiliates: [],
        success: false,
        submitting: false,
        current: 'representation',
        representativeTypes: [],
        isFetchingAffiliates: false,
      };
    },

    setRepresentative(state, {payload}) {
        return {...state, representative: payload}
    },

    saveCurrentStep(state, { payload }) {
      return {
        ...state,
        current: payload,
      };
    },

    setRepresentativeTypes(state, { payload }) {
      return { ...state, representativeTypes: payload };
    },

    saveAffiliate(state, { payload }) {
      return { ...state, affiliate: payload };
    },

    saveAffiliates(state, { payload }) {
      return { ...state, affiliates: payload.results, isFetchingAffiliates: false };
    },

    setLoadingAffiliates(state, { payload }) {
      return { ...state, isFetchingAffiliates: payload };
    },

    saveUsers(state, { payload }) {
      return { ...state, users: payload.results, isFetchingUsers: false };
    },

    setLoadingUsers(state, { payload }) {
      return { ...state, isFetchingUsers: payload.results };
    },

    setSubmitting(state, { payload }) {
      return { ...state, submitting: payload };
    },

    setSuccess(state, { payload }) {
      return { ...state, success: true, representative: payload };
    },

    saveStepFormData(state, { payload }) {
      return {
        ...state,
        step: {
          ...(state as StateType).step,
          ...payload,
        },
      };
    },
  },
};

export default Model;
